import _ from 'lodash';
import { getDistanceGoogleApi, getNearbyStores } from '../redux/Actions/StoresAction';
import { store } from '../redux/Store';
import Strings from '../constants/Strings';
const base32 = '0123456789bcdefghjkmnpqrstuvwxyz'; // (geohash-specific) Base32 map
export let geoToSearch = [];
const earthRadius = 6371;
let latitude = '';
let longitude = '';
class Geohash {
    /**
     * Encodes latitude/longitude to geohash, either to specified precision or to automatically
     * evaluated precision.
     *
     * @param   {number} lat - Latitude in degrees.
     * @param   {number} lon - Longitude in degrees.
     * @param   {number} [precision] - Number of characters in resulting geohash.
     * @returns {string} Geohash of supplied latitude/longitude.
     * @throws  Invalid geohash.
     *
     * @example
     *     const geohash = Geohash.encode(52.205, 0.119, 7); // => 'u120fxw'
     */
    static encode(lat, lon, precision) {
        // infer precision?
        if (typeof precision == 'undefined') {
            // refine geohash until it matches precision of supplied lat/lon
            for (let p = 1; p <= 12; p++) {
                const hash = Geohash.encode(lat, lon, p);
                const posn = Geohash.decode(hash);
                if (posn.lat == lat && posn.lon == lon) {
                    return hash;
                }
            }
            precision = 12; // set to maximum
        }
        lat = Number(lat);
        lon = Number(lon);
        precision = Number(precision);
        if (isNaN(lat) || isNaN(lon) || isNaN(precision)) {
            throw new Error('Invalid geohash');
        }
        let idx = 0; // index into base32 map
        let bit = 0; // each char holds 5 bits
        let evenBit = true;
        let geohash = '';
        let latMin = -90, latMax = 90;
        let lonMin = -180, lonMax = 180;
        while (geohash.length < precision) {
            if (evenBit) {
                // bisect E-W longitude
                const lonMid = (lonMin + lonMax) / 2;
                if (lon >= lonMid) {
                    idx = idx * 2 + 1;
                    lonMin = lonMid;
                }
                else {
                    idx = idx * 2;
                    lonMax = lonMid;
                }
            }
            else {
                // bisect N-S latitude
                const latMid = (latMin + latMax) / 2;
                if (lat >= latMid) {
                    idx = idx * 2 + 1;
                    latMin = latMid;
                }
                else {
                    idx = idx * 2;
                    latMax = latMid;
                }
            }
            evenBit = !evenBit;
            if (++bit == 5) {
                // 5 bits gives us a character: append it and start over
                geohash += base32.charAt(idx);
                bit = 0;
                idx = 0;
            }
        }
        return geohash;
    }
    /**
     * Decode geohash to latitude/longitude (location is approximate centre of geohash cell,
     *     to reasonable precision).
     *
     * @param   {string} geohash - Geohash string to be converted to latitude/longitude.
     * @returns {{lat:number, lon:number}} (Center of) geohashed location.
     * @throws  Invalid geohash.
     *
     * @example
     *     const latlon = Geohash.decode('u120fxw'); // => { lat: 52.205, lon: 0.1188 }
     */
    static decode(geohash) {
        const bounds = Geohash.bounds(geohash); // <-- the hard work
        // now just determine the centre of the cell...
        const latMin = bounds.sw.lat, lonMin = bounds.sw.lon;
        const latMax = bounds.ne.lat, lonMax = bounds.ne.lon;
        // cell centre
        let lat = (latMin + latMax) / 2;
        let lon = (lonMin + lonMax) / 2;
        // round to close to centre without excessive precision: ⌊2-log10(Δ°)⌋ decimal places
        lat = lat.toFixed(Math.floor(2 - Math.log(latMax - latMin) / Math.LN10));
        lon = lon.toFixed(Math.floor(2 - Math.log(lonMax - lonMin) / Math.LN10));
        return {
            lat: Number(lat),
            lon: Number(lon),
        };
    }
}
/**
 * <p>Represents a point on the surface of a sphere. (The Earth is almost
 * spherical.)</p>
 *
 * <p>To create an instance, call one of the static methods fromDegrees() or
 * fromRadians().</p>
 *
 * <p>This code was originally published at
 * <a href="http://JanMatuschek.de/LatitudeLongitudeBoundingCoordinates#Java">
 * http://JanMatuschek.de/LatitudeLongitudeBoundingCoordinates#Java</a>.</p>
 *
 * @author Jan Philip Matuschek
 * @version 22 September 2010
 */
class GeoLocation {
    constructor() {
        this.radLat = 0; // latitude in radians
        this.radLon = 0; // longitude in radians
        this.degLat = 0; // latitude in degrees
        this.degLon = 0; // longitude in degrees
        this.MIN_LAT = -90 * (Math.PI / 180); // -PI/2
        this.MAX_LAT = 90 * (Math.PI / 180); //  PI/2
        this.MIN_LON = -180 * (Math.PI / 180); // -PI
        this.MAX_LON = 180 * (Math.PI / 180); //  PI
    }
    /**
     * @param latitude the latitude, in degrees.
     * @param longitude the longitude, in degrees.
     */
    static fromDegrees(latitude, longitude) {
        latitude = Number(latitude);
        longitude = Number(longitude);
        let result = new GeoLocation();
        result.radLat = latitude * (Math.PI / 180);
        result.radLon = longitude * (Math.PI / 180);
        result.degLat = latitude;
        result.degLon = longitude;
        result.checkBounds();
        return result;
    }
    /**
     * @param latitude the latitude, in radians.
     * @param longitude the longitude, in radians.
     */
    static fromRadians(latitude, longitude) {
        let result = new GeoLocation();
        result.radLat = latitude;
        result.radLon = longitude;
        result.degLat = latitude * (180 / Math.PI);
        result.degLon = longitude * (180 / Math.PI);
        result.checkBounds();
        return result;
    }
    checkBounds() {
        if (this.radLat < this.MIN_LAT ||
            this.radLat > this.MAX_LAT ||
            this.radLon < this.MIN_LON ||
            this.radLon > this.MAX_LON) {
            throw 'Invalid bounds';
        }
    }
    /**
     * @return the latitude, in degrees.
     */
    getLatitudeInDegrees() {
        return this.degLat;
    }
    /**
     * @return the longitude, in degrees.
     */
    getLongitudeInDegrees() {
        return this.degLon;
    }
    /**
     * <p>Computes the bounding coordinates of all points on the surface
     * of a sphere that have a great circle distance to the point represented
     * by this GeoLocation instance that is less or equal to the distance
     * argument.</p>
     * <p>For more information about the formulae used in this method visit
     * <a href="http://JanMatuschek.de/LatitudeLongitudeBoundingCoordinates">
     * http://JanMatuschek.de/LatitudeLongitudeBoundingCoordinates</a>.</p>
     * @param distance the distance from the point represented by this
     * GeoLocation instance. Must me measured in the same unit as the radius
     * argument.
     * @param radius the radius of the sphere, e.g. the average radius for a
     * spherical approximation of the figure of the Earth is approximately
     * 6371.01 kilometers.
     * @return an array of two GeoLocation objects such that:<ul>
     * <li>The latitude of any point within the specified distance is greater
     * or equal to the latitude of the first array element and smaller or
     * equal to the latitude of the second array element.</li>
     * <li>If the longitude of the first array element is smaller or equal to
     * the longitude of the second element, then
     * the longitude of any point within the specified distance is greater
     * or equal to the longitude of the first array element and smaller or
     * equal to the longitude of the second array element.</li>
     * <li>If the longitude of the first array element is greater than the
     * longitude of the second element (this is the case if the 180th
     * meridian is within the distance), then
     * the longitude of any point within the specified distance is greater
     * or equal to the longitude of the first array element
     * <strong>or</strong> smaller or equal to the longitude of the second
     * array element.</li>
     * </ul>
     */
    boundingCoordinates(distance, radius) {
        // angular distance in radians on a great circle
        let radDist = distance / radius;
        let minLat = this.radLat - radDist;
        let maxLat = this.radLat + radDist;
        var minLon, maxLon;
        if (minLat > this.MIN_LAT && maxLat < this.MAX_LAT) {
            let deltaLon = Math.asin(Math.sin(radDist) / Math.cos(this.radLat));
            minLon = this.radLon - deltaLon;
            if (minLon < this.MIN_LON) {
                minLon += 2 * Math.PI;
            }
            maxLon = this.radLon + deltaLon;
            if (maxLon > this.MAX_LON) {
                maxLon -= 2 * Math.PI;
            }
        }
        else {
            // a pole is within the distance
            minLat = Math.max(minLat, this.MIN_LAT);
            maxLat = Math.min(maxLat, this.MAX_LAT);
            minLon = this.MIN_LON;
            maxLon = this.MAX_LON;
        }
        let retVal = [];
        retVal.push(GeoLocation.fromRadians(minLat, minLon));
        retVal.push(GeoLocation.fromRadians(maxLat, maxLon));
        return retVal;
    }
}
const setLocation = (lat, lon) => {
    latitude = lat;
    longitude = lon;
};
const calculateGH = (data) => {
    const { lat, lon, distanceFromCurrentLoc = 5, geohashprecision = 4, callApi = true } = data;
    setLocation(lat, lon);
    geoToSearch = [];
    let bsPoint = GeoLocation.fromDegrees(lat, lon);
    let arr = bsPoint.boundingCoordinates(distanceFromCurrentLoc, earthRadius);
    let ghb = Geohash.encode(lat, lon, geohashprecision);
    let gh1 = Geohash.encode(arr[0].getLatitudeInDegrees(), arr[0].getLongitudeInDegrees(), geohashprecision);
    let gh2 = Geohash.encode(arr[1].getLatitudeInDegrees(), arr[1].getLongitudeInDegrees(), geohashprecision);
    let gh3 = Geohash.encode(arr[0].getLatitudeInDegrees(), arr[1].getLongitudeInDegrees(), geohashprecision);
    let gh4 = Geohash.encode(arr[1].getLatitudeInDegrees(), arr[0].getLongitudeInDegrees(), geohashprecision);
    geoToSearch.push(ghb);
    if (!geoToSearch.includes(gh1)) {
        geoToSearch.push(gh1);
    }
    if (!geoToSearch.includes(gh2)) {
        geoToSearch.push(gh2);
    }
    if (!geoToSearch.includes(gh3)) {
        geoToSearch.push(gh3);
    }
    if (!geoToSearch.includes(gh4)) {
        geoToSearch.push(gh4);
    }
    if (callApi) {
        geoToSearch.forEach(getData);
    }
    return geoToSearch;
};
const getData = (geohash, index) => {
    const updatedStores = index === geoToSearch?.length - 1;
    store?.dispatch(getNearbyStores(geohash, updatedStores));
};
const checkAndProceed = (data) => {
    const { stores, disposition = '', distanceMetrics, driveTime, distanceInMtrs, noOfStores, channel } = data;
    // if (geoChecker.length === geoToSearch.length) {
    let sortedList = [];
    if (stores?.length > 1) {
        sortedList = stores.sort(compare);
    }
    else {
        sortedList = [
            {
                ...stores[0],
                distance: calculateDistanceAlgo(latitude, longitude, stores[0]?.location?.latitude, stores[0]?.location?.longitude),
            },
        ];
    }
    return getSortedNearbyStores({
        stores: sortedList,
        numberOfStores: noOfStores,
        disposition,
        distanceMetrics,
        driveTime,
        distanceInMtrs,
        channel
    });
    // for (let i = 0; i < sortedList.length; i++) {
    //     if (checkDelivery(latitude, longitude, sortedList[i].polygon)) {
    //         break;
    //     }
    // }
    // }
};
function compare(a, b) {
    a.distance = calculateDistanceAlgo(latitude, longitude, a?.location?.latitude, a?.location?.longitude);
    b.distance = calculateDistanceAlgo(latitude, longitude, b?.location?.latitude, b?.location?.longitude);
    return a.distance - b.distance;
}
function calculateDistanceAlgo(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // metres
    const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // in metres
    return d;
}
// function checkDelivery(lat, lon, polygon) {
//   if (polygon) {
//       var triangleCoords = [];
//       var pts = polygon.split(' ');
//       if (pts && pts.length > 0) {
//           for (let i = 0; i < pts.length; i++) {
//               if (pts[i]) {
//                   let latnLong = pts[i].split('|');
//                   triangleCoords.push({ lat: Number(latnLong[0]), lng: Number(latnLong[1]) })
//               }
//           }
//           if (triangleCoords.length > 0) {
//               var bermudaTriangle = new google.maps.Polygon({ paths: triangleCoords });
//               if (google.maps.geometry.poly.containsLocation(new google.maps.LatLng(lat, lon), bermudaTriangle))
//                   return true;
//           }
//       }
//   }
//   return false;
// }
const calculateDistance = ({ origin, stores, googleConfig, distanceMetrics = Strings.km, setStatusFlag, callApi = true, }) => {
    const { lat: originLat, lon: originLon } = origin;
    let destinationsCoordsList = [];
    let listOfStores = [];
    let storesSets = [];
    let coords = [];
    const originCoords = originLat + ',' + originLon;
    destinationsCoordsList = stores
        .map((destination) => {
        const lat = destination?.location?.latitude;
        const lon = destination?.location?.longitude;
        return lat + Strings.commaSeparator + lon;
    })
        .map((data, i) => {
        if (i % 25 < 25) {
            listOfStores.push(stores[i]);
            coords.push(data);
        }
        if (i % 25 === 24 || i === stores.length - 1) {
            const destList = coords.join(Strings.pipeSeparator);
            storesSets.push(listOfStores);
            coords = [];
            listOfStores = [];
            return destList;
        }
    })
        .filter((data) => data);
    if (callApi) {
        destinationsCoordsList.forEach((destination, i) => {
            store.dispatch(getDistanceGoogleApi({
                origin: originCoords,
                destination: destination,
                store: storesSets[i],
                googleConfig,
                distanceMetrics,
                setStatusFlag,
            }));
        });
    }
    return { destinationsCoordsList, originCoords, storesSets, googleConfig, distanceMetrics, setStatusFlag };
};
const getSortedNearbyStores = (data) => {
    const { stores, numberOfStores = 5, disposition = '', distanceMetrics, driveTime, distanceInMtrs, channel = Strings.app } = data;
    const dispositionType = disposition.toLowerCase() === Strings.delivery ? Strings.deliveryDistance : Strings.pickupDistance;
    const nearbyStores = stores?.length > 0 &&
        stores.filter((store) => {
            const isAvailable = disposition
                ? store?.availableServicesPerChannel
                    ?.filter((services) => services.channel === channel)
                    .map((data) => data?.services?.toString().toLowerCase().indexOf(disposition?.toLowerCase()) >= 0)
                : [true];
            const service = store.additionalAttributes?.filter((service) => service.key === dispositionType);
            if (isAvailable?.[0] &&
                ((!distanceInMtrs && parseFloat(service[0]?.value) >= _.get(store, Strings.distance, store?.distance)) ||
                    (distanceInMtrs && distanceInMtrs >= _.get(store, Strings.distance, store?.distance))) &&
                (disposition.toLowerCase() !== Strings.delivery ||
                    (disposition.toLowerCase() === Strings.delivery &&
                        _.get(store, 'distance.duration.value', 0) <= driveTime * 60))) {
                return store;
            }
        });
    return nearbyStores?.length > 0
        ? _.sortBy(nearbyStores, (item) => _.get(item, Strings.distance, item?.distance) &&
            (disposition.toLowerCase() !== Strings.delivery ||
                (disposition.toLowerCase() === Strings.delivery && _.get(store, 'distance.duration.value', 0)))).filter((data, i) => i < numberOfStores)
        : null;
};
export default {
    Geohash,
    GeoLocation,
    calculateGH,
    calculateDistance,
    getSortedNearbyStores,
    checkAndProceed,
    compare,
    setLocation,
    calculateDistanceAlgo,
};
